import { useCallback, useMemo } from 'react';
import { useAppStore } from '@/store';
import useAuth from '@/hooks/useAuth';
import { resetCanvasLoadState } from '@/utils/canvas/canvasLoadState';
import openRegistrationModal from '@/utils/user/openRegistrationModal';
import sendAiGenScreenActionAnalytics from '@/utils/analytics/sendAiGenScreenActionAnalytics';
import getScreenType from '@/utils/analytics/getScreenType';
import { AiGenScreenAction } from '@/types/analytics';
import { closeMiniapp } from '@/utils/editor/closeAndNavigate';
import extractUrlParams from '@/utils/ui/extractUrlParams';
import { DeeplinkKeys } from '@/consts/deepLinks';
import { Screen } from '@/types';

import type { IAppStore } from '@/store';

// External selector for stable reference
const selector = (store: IAppStore) => ({
  screen: store.screen,
  setScreen: store.setScreen,
  previousScreen: store.previousScreen,
});

/**
 * Hook for Header component state and handlers.
 */
const useHeaderState = () => {
  const { screen, setScreen, previousScreen } = useAppStore(selector);
  const { isAuthorized } = useAuth();

  // Read hideBackButton deeplink param once (stable across re-renders)
  const hideBackButton = useMemo(() => {
    const params = extractUrlParams([DeeplinkKeys.hideBackButton]);
    return params[DeeplinkKeys.hideBackButton] === 'true';
  }, []);

  // Back button shown on mobile only (CSS handles hiding on desktop)
  // Hide on library screen, and on create screen when hideBackButton deeplink param is true
  const showBackButton =
    screen !== Screen.Library && !(screen === Screen.Create && hideBackButton);

  // Generations button shown on mobile only (CSS handles hiding on desktop)
  // Only show for library and create screens
  const showGenerationsButton =
    screen === Screen.Library || screen === Screen.Create;

  const handleBack = useCallback(() => {
    // Send BACK analytics
    sendAiGenScreenActionAnalytics({
      action: AiGenScreenAction.BACK,
      screen_type: getScreenType(),
    });

    if (screen === Screen.Create) {
      // Don't reset - preserve uploaded media for when user selects another preset
      // Reset canvas load state so images can be refreshed on next preset selection
      // Gallery stays mounted, so its scroll/active category are preserved
      // natively — no shouldScrollToActiveTab flag needed (and setting it would
      // pin the active tab, since nothing remounts to consume/reset it).
      resetCanvasLoadState();
      setScreen(Screen.Library);
    } else if (screen === Screen.Files) {
      // Go back to previous screen if available, otherwise library
      if (previousScreen && previousScreen !== Screen.Files) {
        setScreen(previousScreen);
      } else {
        setScreen(Screen.Library);
      }
    }
  }, [screen, setScreen, previousScreen]);

  const handleGenerationsClick = useCallback(() => {
    setScreen(Screen.Files);
  }, [setScreen]);

  const handleCloseApp = useCallback(() => {
    // Send CANCEL analytics
    sendAiGenScreenActionAnalytics({
      action: AiGenScreenAction.CANCEL,
      screen_type: getScreenType(),
    });

    try {
      closeMiniapp();
    } catch {
      // Failed to close miniapp - SDK may not be available
    }
  }, []);

  const handleLogin = useCallback(() => {
    openRegistrationModal();
  }, []);

  /**
   * Get title based on screen (for mobile)
   */
  const getMobileTitle = useCallback(() => {
    switch (screen) {
      case Screen.Library:
        return 'GenAI Studio';
      case Screen.Create:
        return 'Select WoW Presets';
      case Screen.Loading:
        return 'Generating...';
      case Screen.Files:
        return 'Your Creations';
      default:
        return 'GenAI Studio';
    }
  }, [screen]);

  return {
    screen,
    isAuthorized,
    showBackButton,
    showGenerationsButton,
    handleBack,
    handleGenerationsClick,
    handleCloseApp,
    handleLogin,
    getMobileTitle,
  };
};

export default useHeaderState;
